<template>
  <div class="mcontainer">
    <!-- Header -->
    <div class="header-bar">
      <div class="title">
        <h2>Connect Alumni</h2>
        <p class="subtitle">Jelajahi alumni berdasarkan jurusan</p>
      </div>

      <div class="actions">
        <!-- Filter Faculty -->
        <select
          class="form-control"
          v-model="faculty"
          @change="fetchAlumni"
        >
          <option value="">Semua Jurusan</option>
          <option v-for="f in faculties" :key="f" :value="f">
            {{ f }}
          </option>
        </select>

        <!-- Search -->
        <input
          type="text"
          class="search-input"
          placeholder="Cari nama atau email..."
          v-model="search"
          @input="onSearch"
        />
      </div>
    </div>

    <!-- Card -->
    <div class="card">
      <!-- Loading -->
      <div v-if="loading" class="loading-wrap">
        Loading...
      </div>

      <!-- Empty -->
      <div v-else-if="alumni.length === 0" class="empty-state">
        <Icon icon="mdi:account-search-outline" width="22" height="22" />
        <span>Tidak ada alumni ditemukan</span>
      </div>

      <!-- List -->
      <template v-else>
        <div class="result-info" v-if="total > 0">
          Menampilkan {{ alumni.length }} dari {{ total }} alumni
        </div>
        <div class="grid">
          <div
            v-for="user in alumni"
            :key="user.id"
            class="user-card"
            @click="openProfileModal(user)"
          >
            <div class="avatar">
              {{ user.fullname.charAt(0).toUpperCase() }}
            </div>

            <div class="user-info">
              <div class="name">{{ user.fullname }}</div>
              <div class="email">{{ user.email }}</div>
              <span class="badge" v-if="user.faculity">
                {{ user.faculity }}
              </span>
            </div>
          </div>
        </div>
        <div class="load-more-wrap" v-if="hasMore">
          <button
            class="load-more-btn"
            :disabled="loadingMore"
            @click="loadMore"
          >
            <span v-if="loadingMore">Memuat...</span>
            <span v-else>Load More</span>
          </button>
        </div>
      </template>
    </div>

    <!-- Modal profil alumni -->
    <div v-if="profileModalShow" class="modal-overlay" @click.self="closeProfileModal">
      <div class="modal-panel profile-modal">
        <div class="modal-header">
          <h3>Profil Alumni</h3>
          <button class="icon-btn-close" @click="closeProfileModal" aria-label="Tutup">
            <Icon icon="mdi:close" width="20" height="20" />
          </button>
        </div>
        <div class="modal-body" v-if="selectedUser">
          <div class="profile-modal-content">
            <div class="profile-avatar">
              {{ selectedUser.fullname.charAt(0).toUpperCase() }}
            </div>
            <h4 class="profile-name">{{ selectedUser.fullname }}</h4>
            <div class="profile-fields">
              <div class="field" v-if="selectedUser.email">
                <Icon icon="mdi:email-outline" class="field-icon" />
                <span>{{ selectedUser.email }}</span>
              </div>
              <div class="field" v-if="selectedUser.faculity">
                <Icon icon="mdi:school-outline" class="field-icon" />
                <span>{{ selectedUser.faculity }}</span>
              </div>
              <div class="field" v-if="selectedUser.generation">
                <Icon icon="mdi:calendar-outline" class="field-icon" />
                <span>Angkatan {{ selectedUser.generation }}</span>
              </div>
              <div class="field" v-if="selectedUser.phone">
                <Icon icon="mdi:phone-outline" class="field-icon" />
                <span>{{ selectedUser.phone }}</span>
              </div>
              <div class="field" v-if="selectedUser.pekerjaan">
                <Icon icon="mdi:briefcase-outline" class="field-icon" />
                <span>{{ selectedUser.pekerjaan }}</span>
              </div>
              <div class="field" v-if="selectedUser.domisili">
                <Icon icon="mdi:map-marker-outline" class="field-icon" />
                <span>{{ selectedUser.domisili }}</span>
              </div>
              <div class="field" v-if="selectedUser.ig_username">
                <Icon icon="mdi:instagram" class="field-icon" />
                <span>@{{ selectedUser.ig_username }}</span>
              </div>
              <div class="field" v-if="selectedUser.address">
                <Icon icon="mdi:home-outline" class="field-icon" />
                <span>{{ selectedUser.address }}</span>
              </div>
            </div>
            <p class="profile-about" v-if="selectedUser.about">{{ selectedUser.about }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { Icon } from "@iconify/vue2";

let debounceTimer = null;

export default {
  name: "Connect",
  components: { Icon },

  data() {
    return {
      alumni: [],
      faculty: "",
      search: "",
      loading: false,
      loadingMore: false,

      faculties: [],
      authFaculty: "",

      page: 1,
      lastPage: 1,
      total: 0,
      perPage: 20,

      profileModalShow: false,
      selectedUser: null
    };
  },

  computed: {
    hasMore() {
      return this.page < this.lastPage;
    }
  },

  mounted() {
    this.setAuthFaculty();
    this.fetchAlumni();
  },

  methods: {
    setAuthFaculty() {
      const user = JSON.parse(localStorage.getItem("user"));
      if (user && user.faculity) {
        this.authFaculty = user.faculity;
        this.faculty = user.faculity;
      }
    },

    async fetchAlumni(reset = true) {
      if (reset) {
        this.page = 1;
        this.loading = true;
      }
      try {
        const params = new URLSearchParams();
        params.set("type", "alumni");
        params.set("per_page", this.perPage);
        params.set("page", this.page);

        if (this.faculty) params.set("faculity", this.faculty);
        if (this.search) params.set("search", this.search);

        const res = await axios.get(
          `user/all?${params.toString()}`
        );

        const data = res?.data?.data || [];
        if (reset) {
          this.alumni = data;
          this.lastPage = res?.data?.last_page ?? 1;
          this.total = res?.data?.total ?? 0;

          // generate faculties dinamis (hanya saat reset)
          const facSet = new Set();
          data.forEach(u => {
            if (u.faculity) facSet.add(u.faculity);
          });
          this.faculties = Array.from(facSet).sort();
        } else {
          this.alumni = [...this.alumni, ...data];
        }
      } catch (e) {
        console.error("Fetch alumni error", e);
        if (reset) this.alumni = [];
      } finally {
        this.loading = false;
        this.loadingMore = false;
      }
    },

    async loadMore() {
      if (this.loadingMore || !this.hasMore) return;
      this.loadingMore = true;
      this.page += 1;
      await this.fetchAlumni(false);
    },

    onSearch() {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        this.fetchAlumni(true);
      }, 400);
    },

    openProfileModal(user) {
      this.selectedUser = user;
      this.profileModalShow = true;
    },

    closeProfileModal() {
      this.profileModalShow = false;
      this.selectedUser = null;
    }
  }
};
</script>

<style scoped>
/* Header */
.header-bar {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 16px;
}
.title h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}
.subtitle {
  margin: 4px 0 0;
  font-size: 13px;
  color: #6b7280;
}
.actions {
  display: flex;
  gap: 10px;
}

/* Inputs */
.form-control,
.search-input {
  height: 38px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 0 12px;
  font-size: 14px;
}

/* Card */
.card {
  background: #fff;
  border: 1px solid #eef2f7;
  border-radius: 14px;
  padding: 16px;
}

/* Grid */
.grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 12px;
}

/* User card */
.user-card {
  display: flex;
  gap: 12px;
  padding: 14px;
  border-radius: 12px;
  border: 1px solid #eef2f7;
  background: #fafafa;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;
}
.user-card:hover {
  background: #f1f5f9;
  border-color: #e2e8f0;
}
.avatar {
  height: 44px;
  width: 44px;
  border-radius: 50%;
  background: #2563eb;
  color: #fff;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}
.user-info {
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: 4px;
}
.name {
  font-weight: 600;
}
.email {
  font-size: 13px;
  color: #6b7280;
}
.badge {
  display: inline-block;
  margin-top: 4px;
  font-size: 12px;
  padding: 5px 15px;
  border-radius: 9999px;
  background: #ecfdf5;
  color: #065f46;
}

/* Result info */
.result-info {
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 12px;
}

/* Load more */
.load-more-wrap {
  display: flex;
  justify-content: center;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #eef2f7;
}
.load-more-btn {
  padding: 10px 24px;
  font-size: 14px;
  font-weight: 500;
  color: #2563eb;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.2s;
}
.load-more-btn:hover:not(:disabled) {
  background: #dbeafe;
}
.load-more-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* States */
.loading-wrap {
  display: flex;
  justify-content: center;
  color: #6b7280;
}
.empty-state {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #9ca3af;
}

/* Modal profil */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.25);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 40px 16px;
  z-index: 999;
}
.modal-panel {
  width: 100%;
  max-width: 480px;
  background: #fff;
  border-radius: 14px;
  border: 1px solid #eef2f7;
  box-shadow: 0 16px 40px rgba(2, 6, 23, 0.15);
  overflow: hidden;
  animation: modalIn 0.15s ease-out;
}
@keyframes modalIn {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid #eef2f7;
}
.modal-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}
.icon-btn-close {
  padding: 4px;
  background: none;
  border: none;
  cursor: pointer;
  color: #64748b;
  border-radius: 6px;
}
.icon-btn-close:hover {
  background: #f1f5f9;
  color: #334155;
}
.modal-body {
  padding: 20px 16px;
}
.modal-footer {
  display: flex;
  justify-content: flex-end;
  padding: 12px 16px;
  border-top: 1px solid #eef2f7;
}
.profile-modal-content {
  text-align: center;
}
.profile-avatar {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #2563eb;
  color: #fff;
  font-size: 28px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px;
}
.profile-name {
  margin: 0 0 16px;
  font-size: 18px;
  font-weight: 600;
}
.profile-fields {
  display: flex;
  flex-direction: column;
  gap: 10px;
  text-align: left;
  margin-bottom: 16px;
}
.profile-fields .field {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: #475569;
}
.field-icon {
  flex-shrink: 0;
  color: #64748b;
}
.profile-about {
  font-size: 14px;
  color: #475569;
  line-height: 1.6;
  white-space: pre-line;
  text-align: left;
  margin: 0 0 16px;
  padding: 12px;
  background: #f8fafc;
  border-radius: 10px;
}
.btn-profile-link {
  display: inline-block;
  text-decoration: none;
  margin-top: 8px;
}

/* Responsive */
@media (max-width: 768px) {
  .header-bar {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  .actions {
    width: 100%;
    flex-direction: column;
  }
}
</style>
